import axios, { AxiosResponse } from 'axios';

import { axiosConfigAuth } from '../middleware';
import {
  API_CRM_ANALYTICS_COHORTS,
  API_CRM_ANALYTICS_SCHOOLS,
  API_CRM_ANALYTICS_SUMMARY,
} from '../urls';

export type TCrmGranularity = 'day' | 'week' | 'month' | 'quarter' | 'year';

// Статус школы за день по ночной детализации (бэк: CrmAnalytics::STATUS)
export type TCrmStatus = 'test' | 'paying' | 'debt' | 'grace_alive' | 'suspended' | 'grace_dead';
export type TCrmSchoolStatus = TCrmStatus | 'disconnected';

export type TCrmSignal = 'debt' | 'balance_low' | 'no_activity' | 'stopped_paying' | 'works_not_paying';

export type TCrmStatuses = Record<TCrmStatus, number>;

export interface TCrmPeriod {
  date_from: string;
  date_to: string;
  granularity: TCrmGranularity;
}

export interface TCrmSummaryRow {
  bucket: string;
  bucket_end: string;
  snapshot_day: string | null;
  statuses: TCrmStatuses | null;
  balance_main: number | null;
  paying_start: number;
  cash_real: number;
  cash_bonus: number;
  cash_excluded: number;
  payers: number;
  accrual_real: number;
  accrual_bonus: number;
  paying_schools: number;
  partner_commission: number;
  new_schools: number;
  cohort_paid: number;
  new_paying: number;
  returned: number;
  churned: number;
  churned_grace: number;
  churned_stopped: number;
  churn_final: boolean;
  cohort_conversion: number | null;
  churn_rate: number | null;
  arppu: number | null;
}

export interface TCrmSummaryTotals {
  cash_real: number;
  cash_bonus: number;
  cash_excluded: number;
  accrual_real: number;
  accrual_bonus: number;
  partner_commission: number;
  new_schools: number;
  cohort_paid: number;
  new_paying: number;
  returned: number;
  churned: number;
  churned_grace: number;
  churned_stopped: number;
  paying_start: number;
  paying_end: number | null;
  balance_main: number | null;
  cohort_conversion: number | null;
  churn_rate: number | null;
}

export interface TCrmSnapshot {
  day: string;
  statuses: TCrmStatuses;
  balance_main: number;
}

export interface TCrmSummary {
  granularity: TCrmGranularity;
  date_from: string;
  date_to: string;
  last_day: string;
  churn_days: number;
  rows: TCrmSummaryRow[];
  totals: TCrmSummaryTotals;
  current: TCrmSnapshot | null;
}

export interface TCrmCohortWindow {
  days: number;
  paid: number;
  conversion: number | null;
  mature: boolean;
}

export interface TCrmCohortRow {
  bucket: string;
  bucket_end: string;
  connected: number;
  paid_ever: number;
  conversion: number | null;
  median_days_to_pay: number | null;
  windows: TCrmCohortWindow[];
  now: Record<TCrmSchoolStatus, number>;
}

export interface TCrmCohorts {
  granularity: TCrmGranularity;
  date_from: string;
  date_to: string;
  last_day: string;
  rows: TCrmCohortRow[];
  totals: { connected: number; paid_ever: number; conversion: number | null };
}

export interface TCrmSchoolRow {
  id: number;
  name: string | null;
  domain: string | null;
  connected_at: string;
  is_excluded: boolean;
  status: TCrmSchoolStatus | null;
  revenue_30d: number;
  active_users: number;
  payments: number;
  cash_real: number;
  accrual_real: number;
  partner_commission: number;
  first_payment: string | null;
  last_payment: string | null;
  days_to_pay: number | null;
  paid_days: number;
  service_days: number;
  episodes: number;
  last_paid_day: string | null;
  churned: boolean;
  balance_main: number;
  balance_bonus: number;
  access_until: string | null;
  days_left: number | null;
  last_activity: string | null;
  signals: TCrmSignal[];
}

export interface TCrmSchoolsTotals {
  schools: number;
  paid_ever: number;
  paying_now: number;
  churned: number;
  ltv_avg: number | null;
  ltv_median: number | null;
  paid_days_avg: number | null;
  paid_days_median: number | null;
  service_days_avg: number | null;
  service_days_paid_avg: number | null;
  works_not_paying: number;
  works_not_paying_revenue: number;
}

export interface TCrmSchools {
  last_day: string;
  churn_days: number;
  rows: TCrmSchoolRow[];
  totals: TCrmSchoolsTotals;
}

export const crmAnalyticsApi = {
  getSummary: (period: TCrmPeriod): Promise<AxiosResponse<TCrmSummary>> =>
    axios.post(API_CRM_ANALYTICS_SUMMARY, period, axiosConfigAuth()),

  getCohorts: (period: TCrmPeriod): Promise<AxiosResponse<TCrmCohorts>> =>
    axios.post(API_CRM_ANALYTICS_COHORTS, period, axiosConfigAuth()),

  getSchools: (): Promise<AxiosResponse<TCrmSchools>> =>
    axios.get(API_CRM_ANALYTICS_SCHOOLS, axiosConfigAuth()),
};
